import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { AccessibilityNew, AutoAwesomeMotion, Code, Palette, PhoneIphone, Security, Speed} from '@mui/icons-material';

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '24rem',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  padding: theme.spacing(2),
  // maskImage:
  //     "radial-gradient(ellipse at center, rgba(255, 255, 255, 1), transparent 100%)",
}));

const IconRow = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '48rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(5),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'space-between',
    gap: 0,
  },
}));

const RadarContainer = styled(Box)({
  position: 'relative',
  height: '5rem',
  width: '5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
});

const RadarLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '50%',
  top: '50%',
  zIndex: 40,
  height: '5px',
  width: '400px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  background: 'transparent',
  transformOrigin: 'right center',
  animation: 'radar-spin 4s linear infinite',
  '& > div': {
    height: '1px',
    width: '100%',
    background: `linear-gradient(to right, transparent, rgb(234, 55, 54), transparent)`,
  },
  '@keyframes radar-spin': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
    height: '2.8rem',
    width: '2.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '.7rem',
    background: 'linear-gradient(to bottom, rgb(234, 55, 54), rgb(90, 20, 23))',
    boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  }));
  

const RadarCircle = styled(motion.div)<{ size: number }>(({ theme, size }) => ({
  position: 'absolute',
  inset: 0,
  left: '50%',
  top: '50%',
  height: `${size * 4.25}rem`,
  width: `${size * 4.25}rem`,
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  border: `1px solid rgba(234, 55, 54, ${1 - (size + 1) * 0.1})`,
}));

interface IconContainerProps {
  icon: React.ReactNode;
  text: string;
  delay: number;
}

const IconContainer: React.FC<IconContainerProps> = ({ icon, text, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, delay }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
    >
      <IconBox>
        {icon}
      </IconBox>
      <Typography variant="overline" sx={{ fontWeight: 300, color: 'text.secondary', textTransform: "capitalize" }}>
        {text}
      </Typography>
    </motion.div>
  );
};

export default function ServicesRadar() {
  const circles = new Array(8).fill(1);

  return (
    <Container>
      <IconRow>
        <IconContainer icon={<Code sx={{ fontSize: 32, color: 'text.primary' }} />} text="Custom UI Components" delay={0.1} />
        <IconContainer icon={<AutoAwesomeMotion sx={{ fontSize: 32, color: 'text.primary' }} />} text="Animations & Transitions" delay={0.3} />
        <IconContainer icon={<AccessibilityNew sx={{ fontSize: 32, color: 'text.primary' }} />} text="Accessibility Optimization" delay={0.5} />
        </IconRow>
        <IconRow sx={{ maxWidth: '32rem', my: 2 }}>
        <IconContainer icon={<Security sx={{ fontSize: 32, color: 'text.primary' }} />} text="Security Enhancement" delay={0.6} />
        <IconContainer icon={<Speed sx={{ fontSize: 32, color: 'text.primary' }} />} text="Performance Optimization" delay={0.8} />
        </IconRow>
        <IconRow>
        <IconContainer icon={<PhoneIphone sx={{ fontSize: 32, color: 'text.primary' }} />} text="Responsive Design" delay={0.4} />
        <IconContainer icon={<Palette sx={{ fontSize: 32, color: 'text.primary' }} />} text="Theme Customization" delay={0.7} />
        </IconRow>

      <RadarContainer sx={{ position: 'absolute', bottom: '-3rem' }}>
        <RadarLine>
          <div />
        </RadarLine>
        {circles.map((_, idx) => (
          <RadarCircle
            key={`radar-circle-${idx}`}
            size={idx + 1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: idx * 0.1, duration: 0.2 }}
          />
        ))}
      </RadarContainer>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          zIndex: 41,
          height: '1px',
          width: '100%',
          background: 'linear-gradient(to right, transparent, grey.700, transparent)',
        }}
      />
    </Container>
  );
}